/**
 * The pages a person sees before the console proper, all on the same lit frame as Mojo Up AI
 * Cloud's sign-in (AuthFrame): signed out, signing in to an Enterprise organisation, and a console
 * that cannot start. Each says where the person is and offers one thing to do.
 */

import { useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../auth/AuthProvider.tsx';
import { BrandMark } from '../components/cloud/BrandMark.tsx';
import { AuthFrame } from '../components/cloud/SignIn.tsx';
import { Button } from '../components/ui.tsx';
import { isCloud, productName } from '../edition.ts';

/** Who the person signed in to: the organisation on Enterprise, the service on Cloud. */
function placeName(organisation: string | undefined): string {
  return isCloud() ? productName() : (organisation ?? productName());
}

/** "You are signed out": shown after signing out, and at /signed-out, which never signs in by itself. */
export function SignedOutPage() {
  const { discovery, ended, signIn } = useAuth();
  const { search } = useLocation();
  const navigate = useNavigate();
  const idle = ended === 'idle' || new URLSearchParams(search).get('idle') === '1';
  const where = placeName(discovery?.organisation);
  return (
    <AuthFrame title="You are signed out" blurb={idle ? `The console signed you out of ${where} after 25 minutes without activity.` : `You have signed out of ${where}.`} footer="Close this tab, or sign in again whenever you are ready.">
      <Button variant="primary" fullWidth onPress={() => (isCloud() ? navigate('/', { replace: true }) : signIn?.())}>
        Sign in
      </Button>
    </AuthFrame>
  );
}

/**
 * Enterprise, signed out: what this is, and one button on to Microsoft. The title is the product and the organisation
 * is named below it, so an organisation's name (Mojo Up's own, under Mojo Up's mark) never reads as the vendor's brand.
 */
export function EnterpriseSignInPage() {
  const { discovery, signIn } = useAuth();
  const organisation = discovery?.organisation?.trim();
  return (
    <AuthFrame
      title={productName()}
      blurb={organisation ? `${organisation}’s AI workbenches, projects and policies.` : "Your organisation's AI workbenches, projects and policies."}
      footer="You sign in with your work account. Nothing here holds a password."
    >
      <Button variant="tertiary" fullWidth onPress={() => signIn?.()}>
        <BrandMark provider="microsoft" />
        Continue with Microsoft
      </Button>
    </AuthFrame>
  );
}

/** The console could not start: plain words first, the technical detail under Details. */
export function StartFailurePage({ discovery, detail }: { discovery: boolean; detail: string }) {
  return (
    <AuthFrame
      title={discovery ? 'This address is not set up yet' : 'The console cannot start'}
      blurb={discovery ? 'No organisation answers at this address.' : 'Something went wrong while signing you in.'}
    >
      <div className="grid gap-3 text-sm">
        <p>
          {discovery
            ? 'If you run this service, check its address, that it is running, and its discovery record. Otherwise, ask your administrator.'
            : 'Try again in a moment. If it keeps happening, ask your administrator.'}
        </p>
        <Button variant="secondary" fullWidth onPress={() => location.reload()}>
          Try again
        </Button>
        <details className="text-xs text-muted">
          <summary className="cursor-[var(--cursor-interactive)]">Details</summary>
          <p className="mt-1 break-words">{detail}</p>
        </details>
      </div>
    </AuthFrame>
  );
}
